import React, { useState } from 'react'
import { useTheme } from '@mui/system'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { enUS, svSE } from '@mui/x-data-grid-pro/locales'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl } from '@mui/material'
import { Link } from 'react-router-dom'
import * as datefns from 'date-fns'
import ConnectCustomerDialog from '../../pages/admin/users/ConnectCustomerDialog'
import UserApi from '../../api/UserApi'
import useConfirmationDialog from '../dialogs/ConfirmationDialog'
import PropTypes from 'prop-types'
import PasswordField from '../../pages/admin/users/PasswordField'
import UserActionsMenu from './UserActionsMenu'
import { UserCategorySelect } from '../../pages/admin/users/UserCategorySelect'
import { CustomDataGridToolbar } from '../CustomDataGridToolbar'

const gridTextI18n = (locale) => {
    if (locale?.startsWith('sv')) {
        return svSE.components.MuiDataGrid.defaultProps.localeText
    } else {
        return enUS.components.MuiDataGrid.defaultProps.localeText
    }
}

const toggleUserDisabled = async (userId, disabled) => {
    if (disabled) {
        await userApi.adminEnableUser(userId)
    } else {
        await userApi.adminDisableUser(userId)
    }
}

const userColumns = ({
    fields,
    onSetUserCategory,
    onConnectClick,
    onDisconnectClick,
    onToggleDisabled,
    onDeleteUser,
    onChangePassword,
}) => {
    const cols = [
        {
            field: 'name',
            headerName: 'Namn',
            minWidth: 150,
            flex: 1,
        },
        {
            field: 'email',
            headerName: 'Email',
            minWidth: 150,
            flex: 1,
            renderCell: (params) => <Link to={`mailto:${params.value}`}>{params.value}</Link>,
        },
        {
            field: 'customerName',
            headerName: 'Kund',
            minWidth: 150,
            flex: 1,
            renderCell: ({ value, row }) => {
                if (value) {
                    return <Link to={`/admin/customers/${row.customerId}`}>{value}</Link>
                }
                if (onConnectClick) {
                    return (
                        <Button variant='outlined' color='primary' size='small' onClick={() => onConnectClick(row)}>
                            Koppla
                        </Button>
                    )
                }
                return null
            },
        },
        {
            field: 'userCategory',
            headerName: 'Kategori',
            minWidth: 150,
            flex: 1,
            valueGetter: (category) => {
                switch (category) {
                    case 'trial':
                        return 'Testkund'
                    case 'inhouse':
                        return 'Finance Lab'
                    case 'standard':
                        return 'Betalande'
                    default:
                        return '?'
                }
            },
        },
        {
            field: 'disabled',
            headerName: 'Aktiv',
            width: 110,
            valueFormatter: (value) => (value ? 'Inaktiverad' : 'Aktiv'),
        },
        {
            field: 'lastActive',
            headerName: 'Senast aktiv',
            minWidth: 150,
            flex: 1,
            valueFormatter: (value) => (value ? datefns.format(new Date(value), 'yyyy-MM-dd HH:mm:ss') : ''),
        },
        {
            field: 'actions',
            headerName: '',
            minWidth: 30,
            flex: 0.5,
            sortable: false,
            renderCell: ({ row }) => (
                <UserActionsMenu
                    user={row}
                    onSetUserCategory={onSetUserCategory}
                    onDisconnectClick={onDisconnectClick}
                    onToggleDisabled={onToggleDisabled}
                    onDeleteUser={onDeleteUser}
                    onChangePassword={onChangePassword}
                />
            ),
        },
    ]
    if (fields?.length > 0) {
        return cols.filter((col) => fields.includes(col.field))
    }
    return cols
}

const userApi = new UserApi()

function UsersGrid({ fields, users, setUsers }) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const theme = useTheme()
    const { confirm, ConfirmationDialog } = useConfirmationDialog()

    const [selectedUser, setSelectedUser] = useState(null)
    const [connectCustomerDialogOpen, setConnectCustomerDialogOpen] = useState(false)
    const [userCategoryDialogOpen, setUserCategoryDialogOpen] = useState(false)

    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
    const [selectedUserForPassword, setSelectedUserForPassword] = useState(null)
    const [newPassword, setNewPassword] = useState('')

    const handleConnectClick = (user) => {
        setSelectedUser(user)
        setConnectCustomerDialogOpen(true)
    }

    const handleDisconnectClick = async (user) => {
        const confirmation = await confirm('Är du säker på att du vill koppla bort användaren från kunden?')
        if (!confirmation) return
        await userApi.disconnectFromCustomer(user.id)
        const usersCopy = [...users]
        const updatedUser = usersCopy.find((u) => u.id === user.id)
        updatedUser.customerId = null
        updatedUser.customerName = null
        setUsers(usersCopy)
    }

    const handleConnectCustomerDialogClose = () => {
        setConnectCustomerDialogOpen(false)
        setSelectedUser(null)
    }

    const handleUserCategoryDialogClose = () => {
        setUserCategoryDialogOpen(false)
        setSelectedUser(null)
    }

    const handleCustomerSubmit = async (data) => {
        // Logic to update the user with the selected customer
        userApi.connectToCustomer(selectedUser.id, data.customer.id)
        const updatedUsers = await userApi.adminFindAllUsers()
        // Sort the users by name
        updatedUsers.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
        setUsers(updatedUsers)
        handleConnectCustomerDialogClose()
    }

    const handleToggleDisabled = async (userId, disabled) => {
        await toggleUserDisabled(userId, disabled)
        const updatedUsers = await userApi.adminFindAllUsers()
        // Sort the users by name
        updatedUsers.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
        setUsers(updatedUsers)
    }

    const handleDeleteUser = async (userId) => {
        const confirmation = await confirm('Är du säker på att du vill ta bort användaren?')
        if (!confirmation) return
        await userApi.adminDeleteUser(userId)
        const updatedUsers = await userApi.adminFindAllUsers()
        // Sort the users by name
        updatedUsers.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
        setUsers(updatedUsers)
    }

    const handleOpenUserCategoryDialog = (user) => {
        setSelectedUser(user)
        setUserCategoryDialogOpen(true)
    }

    const handleOpenPasswordDialog = (user) => {
        setSelectedUserForPassword(user)
        setPasswordDialogOpen(true)
    }

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false)
        setSelectedUserForPassword(null)
        setNewPassword('')
    }

    const handleChangePassword = async () => {
        if (!newPassword) {
            alert('Please enter a new password.')
            return
        }
        try {
            await userApi.adminSetUserPassword(selectedUserForPassword.id, newPassword)
            alert('Password changed successfully.')
            handleClosePasswordDialog()
        } catch (error) {
            console.error('Failed to change password:', error)
            alert('Failed to change password.')
        }
    }

    const handleUserCategoryChange = async (user, newCategory) => {
        await userApi.adminSetUserCategory(user.id, newCategory)
        const userList = [...users]
        const updatedUser = userList.find((u) => u.id === user.id)
        updatedUser.userCategory = newCategory
        setUsers(userList)
        handleUserCategoryDialogClose()
    }

    return (
        <div style={{ width: '100%' }}>
            <ConfirmationDialog />
            {/* Password Change Dialog */}
            <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog}>
                <DialogTitle>Ändra lösenord för {selectedUserForPassword?.name}</DialogTitle>
                <DialogContent>
                    <PasswordField
                        label='Nytt lösenord'
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoFocus
                        showGeneratePassword={true}
                        sx={{ my: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePasswordDialog} color='secondary'>
                        Avbryt
                    </Button>
                    <Button onClick={handleChangePassword} color='primary'>
                        Ändra
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={userCategoryDialogOpen} onClose={handleUserCategoryDialogClose}>
                <DialogTitle>Ange kategori för {selectedUser?.email}</DialogTitle>
                <DialogContent>
                    {/* create a dropdown with three options: "Betalande", "Testkund", "Inhouse" */}
                    <FormControl fullWidth sx={{ my: 2 }}>
                        <UserCategorySelect
                            onChange={(userCategory) => setSelectedUser({ ...selectedUser, userCategory })}
                            value={selectedUser?.userCategory || null}
                        />
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUserCategoryDialogClose} color='secondary'>
                        Avbryt
                    </Button>
                    <Button
                        onClick={() => {
                            handleUserCategoryChange(selectedUser, selectedUser.userCategory)
                        }}
                        color='primary'
                    >
                        Spara
                    </Button>
                </DialogActions>
            </Dialog>
            <DataGridPro
                sx={{
                    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                        outline: 'none',
                    },
                    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                        outline: 'none',
                    },
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                }}
                rows={users || []}
                columns={userColumns({
                    fields,
                    onSetUserCategory: handleOpenUserCategoryDialog, // Placeholder for user category setter
                    onConnectClick: handleConnectClick,
                    onDisconnectClick: handleDisconnectClick,
                    onToggleDisabled: handleToggleDisabled,
                    onDeleteUser: handleDeleteUser,
                    onChangePassword: handleOpenPasswordDialog,
                })}
                localeText={gridTextI18n(locale)}
                // Use the custom toolbar
                slots={{ toolbar: CustomDataGridToolbar }}
                loading={!users}
                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton',
                    },
                }}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                // Disable column filter options
                disableColumnFilter
                // Disable column menu
                disableColumnMenu
                // Disable row selection on click
                disableRowSelectionOnClick
                showToolbar
            />
            <ConnectCustomerDialog
                open={connectCustomerDialogOpen}
                onClose={handleConnectCustomerDialogClose}
                onSubmit={handleCustomerSubmit}
            />
        </div>
    )
}

UsersGrid.propTypes = {
    fields: PropTypes.arrayOf(PropTypes.string), // Optional array of strings
    users: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // User ID, required
            name: PropTypes.string.isRequired, // User name, required
            email: PropTypes.string, // Email, optional
            customerName: PropTypes.string, // Customer name, optional
            customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Customer ID, optional
            lastActive: PropTypes.string, // ISO date string, optional
            disabled: PropTypes.bool, // Whether the user is disabled, optional
        }),
    ), // Array of user objects, required
    setUsers: PropTypes.func.isRequired, // Function to update the list of users, required
}

export default UsersGrid
